import './index.css'

import * as React from 'react'

import mixpanel from '@disco/api/mixpanel'
import {
  BRIEF_INBOX,
  INBOX_TYPES,
  PERSONAL_LIBRARY,
  SAVED_TO_DISCO,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import type { PaneType } from '@disco/constants/PaneTypes'
import {
  BRIEF_LIST_PANE,
  PLAYLIST_PANE,
  SEARCH_RESULTS_PANE,
  TRACK_PANE,
} from '@disco/constants/PaneTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  BRIEF_ALL_VIEW,
  BRIEF_CLOSED_VIEW,
  BRIEF_MINE_VIEW,
  BRIEF_OPEN_VIEW,
  VIEW_NAMES,
} from '@disco/constants/ViewTypes'
import { getGroupName } from '@disco/utils/groups'

import type { Group } from 'types/group'

import ClipboardInput from 'components/common/clipboard-input'
import { Flex, Link } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Heading from 'components/ui/heading'
import heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import Stack from 'components/ui/stack'
import Text from 'components/ui/text'

import FeatureDescription from '../../feature-description'
import EmptyIcon from './empty-icon'

export type Props = {
  pane: Record<string, any>
  tags?: string[]
  paneType?: PaneType
  group?: Group
}

const EmptyList = (props: Props): React.ReactElement<'div'> => {
  const {
    tags,
    pane: { type, groupType, name, searching, searchError, view, searchTerm },
    group,
  } = props

  let caption: React.ReactNode = ''
  let emptyIcon = <EmptyIcon type={type} />
  switch (type) {
    case SEARCH_RESULTS_PANE:
      if (searching) {
        caption = `Searching...`
      } else if (searchError) {
        caption = 'Search request failed. Please try again.'
      } else if (tags && tags.length) {
        caption = `No items found. Try a different combination of tags.`
      } else if (groupType === TEAM_LIBRARY || groupType === PERSONAL_LIBRARY) {
        if (view === ALBUM_VIEW) {
          caption = (
            <>
              Nothing here yet! Create your first album by toggling the album
              setting on a playlist in the Playlist Creator at the right of the
              app.{' '}
              <InlineLink
                to="https://support.disco.ac/home/creating-albums-on-disco"
                rel="noopener noreferrer"
                target="_blank"
              >
                Need help setting up albums?
              </InlineLink>
            </>
          )
        } else if (view === ARTIST_VIEW) {
          caption = (
            <>
              Nothing here yet! Create your first artist by filling in an artist
              name in the artist metadata field on a track.{' '}
              <InlineLink
                to="https://support.disco.ac/home/artists-and-artist-profiles"
                rel="noopener noreferrer"
                target="_blank"
              >
                Need help setting up artists?
              </InlineLink>
            </>
          )
        } else {
          caption = `Nothing here yet! Create your first playlist in the shortlist area at the right of the app, and fill this with good things.`
        }
      } else if (groupType === SAVED_TO_DISCO) {
        emptyIcon = null
        caption = (
          <FeatureDescription
            icon="saved-to-disco"
            description={
              <>
                One click, no hassle.
                <br />
                Save tracks instantly from DISCO playlists or catalogs—so new
                music is always within reach.
              </>
            }
          >
            <Button
              appearance="primary"
              caption="Learn more"
              linkTarget="_blank"
              link="https://school.disco.ac/article/save-to-disco"
            />
          </FeatureDescription>
        )
      } else if (!groupType) {
        // no results in search
        caption = 'Sorry! No results for that search.'
      } else if (groupType === BRIEF_INBOX) {
        return (
          <div className="ExplorerPane_NoItems_FeatureDescription">
            <FeatureDescription
              icon="edit-document"
              title="No submissions yet"
              description="Submissions for this brief will appear here."
              small
              center
            />
          </div>
        )
      } else if (INBOX_TYPES.includes(group?.group_type) && group?.upload_url) {
        const groupName = getGroupName(group)
        const groupUploadUrl = group?.upload_url
        emptyIcon = <EmptyIcon type={groupType} />
        caption = (
          <Stack space="xsmall">
            <Heading size={600}>This inbox is ready to receive files!</Heading>
            <Text type="paragraph">
              Receive files directly from others. Just share the URL with anyone
              who wants to send you files, regardless of whether they use DISCO
              or not.
              <br />
              <a
                className="text-link"
                href="https://school.disco.ac/article/inboxes-and-receiving-files"
                rel="noopener noreferrer"
                target="_blank"
              >
                Find out more
              </a>
            </Text>

            <ButtonContainer centered>
              <ClipboardInput
                successNotification={{
                  title: `${groupName} link copied!`,
                  message: 'The link has been copied to your clipboard.',
                  level: 'success',
                }}
                customCta={
                  <Button
                    appearance="primary"
                    caption="Copy Link"
                    onClick={() => {
                      mixpanel.sendPureEvent('Copy inbox url', {
                        Url: groupUploadUrl,
                      })
                    }}
                  />
                }
                value={groupUploadUrl}
              />
              <Button
                caption="Open Link"
                linkTarget="_blank"
                link={groupUploadUrl}
              />
            </ButtonContainer>
          </Stack>
        )
      } else if (groupType === 'user') {
        // Channel pane, no results
        const isFolder = !!props.group?.parent
        const isEditable =
          props.group &&
          (props.group.editable || props.group.business_role === 'owner')

        if (searchTerm?.length) {
          const sortType = VIEW_NAMES[view] || ''

          return (
            <Flex pt="48px" justify="center" align="center">
              <FeatureDescription
                icon="search"
                title="It's empty here!"
                description={`No ${sortType} results for "${searchTerm}".`}
                small
              />
            </Flex>
          )
        }

        if (props.group && !isFolder) {
          return (
            <div className="ExplorerPane_NoItems_FeatureDescription">
              <FeatureDescription
                icon="folder"
                title="Organize and connect!"
                description="Use channels to share content and invite your team and partners. Use folders to keep everything neatly organized."
                small
              >
                <ButtonContainer vertical>
                  {isEditable && (
                    <Button
                      appearance="primary"
                      caption="Share channel"
                      link={`/explorer/channels/${props.group.id}/share`}
                    />
                  )}
                  <Link
                    href="https://school.disco.ac/lesson/organize-with-channels"
                    target="_blank"
                    size="2"
                  >
                    Learn more
                  </Link>
                </ButtonContainer>
              </FeatureDescription>
            </div>
          )
        }

        // Remove top icon
        emptyIcon = null
        caption = (
          <FeatureDescription
            icon="upload-file"
            title="It's empty here!"
            description="Add some files from your DISCO, or drag new files and folders from your computer here!"
            small
          />
        )
      } else {
        // regular group pane, no results
        caption = `There aren't any items in ${name}.`
      }

      break

    case BRIEF_LIST_PANE:
      if (searchTerm?.length) {
        return (
          <Flex pt="48px" justify="center" align="center">
            <FeatureDescription
              icon="search"
              title="It's empty here!"
              description={`No results for "${searchTerm}".`}
              small
            />
          </Flex>
        )
      }

      if (props.pane.meta?.briefProjectId) {
        emptyIcon = <EmptyIcon type={groupType} />
        caption = (
          <Stack space="xsmall">
            <Heading size={600}>This project is empty</Heading>
            <Text type="paragraph">Add a brief to this project.</Text>

            <ButtonContainer centered>
              <Button
                appearance="primary"
                caption="New Brief"
                compact
                link={`/brief-editor/new?projectId=${props.pane.meta?.briefProjectId}`}
              />
            </ButtonContainer>
          </Stack>
        )
      } else if (props.pane.view === BRIEF_ALL_VIEW) {
        emptyIcon = <EmptyIcon type={groupType} />
        caption = (
          <Stack space="xsmall">
            <Heading size={600}>Introducing Briefs</Heading>
            <Text type="paragraph">
              A centralized tool to create, manage, and share briefs.
            </Text>

            <ButtonContainer centered>
              <Button
                appearance="primary"
                caption="Get Started"
                onClick={() => {
                  mixpanel.sendPureEvent('Clicked cta button', {
                    cta: 'Briefs getting started',
                  })
                }}
              />
            </ButtonContainer>
          </Stack>
        )
      } else {
        emptyIcon = <EmptyIcon type={groupType} />
        let heading = ''
        switch (props.pane.view) {
          case BRIEF_OPEN_VIEW:
            heading = 'Open Briefs'
            break
          case BRIEF_CLOSED_VIEW:
            heading = 'Closed Briefs'
            break
          case BRIEF_MINE_VIEW:
            heading = 'My Briefs'
            break
          default:
            heading = 'Briefs'
        }

        caption = (
          <Stack space="xsmall">
            <Heading size={600}>{heading}</Heading>
            <Text type="paragraph">
              Nothing in {heading.toLowerCase()} right now
            </Text>
          </Stack>
        )
      }
      break

    case PLAYLIST_PANE:
      caption = (
        <span>
          Nothing here yet! Drag tracks in here to start populating it, or click
          Edit playlist from the{' '}
          <span className="IconText">
            <Icon type="ellipsis-vertical" width="20" height="20" /> menu
          </span>{' '}
          to edit in the shortlist area.
        </span>
      )
      break

    case TRACK_PANE:
      caption = (
        <Stack space="xsmall">
          <Heading size={500}>Not in any playlists</Heading>
          <Text type="paragraph">
            Drag this track to an existing playlist, or create a new one using
            the playlist creator.
          </Text>
        </Stack>
      )
      break

    default:
      caption = `There aren't any items in ${name}`
  }

  return (
    <div className="ExplorerPane_NoItems" data-testid="explorer-pane-no-items">
      {emptyIcon}
      {caption}
    </div>
  )
}

export default EmptyList
